import React from 'react';
import { Field, FieldProps } from 'formik';
import Select, { StylesConfig, components, DropdownIndicatorProps } from 'react-select';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { SvgIcon } from 'helpers/SvgIcon';
import { Field as SitecoreField } from '@sitecore-content-sdk/nextjs';

interface OptionType {
  value: string;
  label: string;
}

interface SelectFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  hideLabel?: boolean;
  isStandalone?: boolean;
}

// Custom dropdown indicator component
export const DropdownIndicator = (props: DropdownIndicatorProps<OptionType>) => {
  return (
    <components.DropdownIndicator {...props}>
      <div
        className="transform"
        style={{
          transform: props.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease-in-out',
        }}
        aria-hidden="true"
      >
        <SvgIcon
          icon="form-dropdown"
          className="text-component-button-tertiary-icon"
          size="xs"
          viewBox="0 0 14 9"
          aria-hidden="true"
        />
      </div>
    </components.DropdownIndicator>
  );
};

// Custom styles for react-select to match our design system
export const getSelectStyles = (
  hasError: boolean,
  isDisabled: boolean
): StylesConfig<OptionType> => ({
  control: (baseStyles, state) => ({
    ...baseStyles,
    minHeight: '48px',
    border: hasError
      ? '1px solid var(--color-text-text-critical)'
      : state.isFocused
        ? '1px solid var(--color-input-border-focused)'
        : '1px solid var(--color-input-border)',
    borderRadius: 'var(--general-border-radius-text-input)',
    backgroundColor: isDisabled
      ? 'var(--color-grayscale-gray-100)'
      : 'var(--color-input-bg-surface)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--color-input-border-focused)' : 'none',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': {
      borderColor: isDisabled ? 'var(--color-input-border)' : 'var(--color-input-border-hover)',
    },
    borderBottomLeftRadius: state.menuIsOpen ? '0' : 'var(--general-border-radius-text-input)',
    borderBottomRightRadius: state.menuIsOpen ? '0' : 'var(--general-border-radius-text-input)',
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--spacing-spacing-12) var(--spacing-spacing-16)',
    fontFamily: 'var(--font-typography-font-family-body)',
    fontSize: 'var(--font-typography-font-size-body-medium)',
    fontWeight: 'normal',
    lineHeight: 'var(--typography-line-height-body-medium, 22px)',
    flexWrap: 'nowrap',
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    color: 'var(--color-text-text-secondary)',
    fontStyle: 'italic',
    fontFamily: 'var(--font-typography-font-family-body)',
    fontSize: 'var(--font-typography-font-size-body-medium)',
    fontWeight: 'normal',
    lineHeight: 'var(--typography-line-height-body-medium, 22px)',
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    color: isDisabled ? 'var(--color-text-text-disabled)' : 'var(--color-text-text)',
    fontFamily: 'var(--font-typography-font-family-body)',
    fontSize: 'var(--font-typography-font-size-body-medium)',
    fontWeight: 'normal',
    lineHeight: 'var(--typography-line-height-body-medium, 22px)',
  }),
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 2,
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    marginTop: '-1px',
    borderRadius: 'var(--general-border-radius-text-input)',
    border: '1px solid var(--color-input-border)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    borderTop: 'none',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    zIndex: 2,
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    paddingLeft: 'var(--spacing-spacing-10)',
    paddingRight: 'var(--spacing-spacing-10)',
    paddingTop: '0',
    paddingBottom: 'var(--spacing-spacing-12)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-spacing-8)',
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    backgroundColor:
      state.isSelected || state.isFocused
        ? 'var(--color-core-color-4-skyblue-lightTint)'
        : 'transparent',
    color: state.isSelected
      ? 'var(--component-button-tertiary-text-focused)'
      : 'var(--component-button-tertiary-text)',
    fontFamily: 'var(--font-typography-font-family-body)',
    fontSize: 'var(--font-typography-font-size-body-medium)',
    lineHeight: 'var(--typography-line-height-xxsmall)',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    borderRadius: 'var(--border-radius-radius-1)',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-core-color-4-skyblue-lightTint)',
      textDecoration: 'none',
    },
    textAlign: 'left',
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: 'var(--spacing-spacing-12) var(--spacing-spacing-16)',
    color: 'var(--color-text-text-secondary)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    padding: 0,
  }),
});

// Custom Formik Field component for react-select
const CustomSelectField: React.FC<
  FieldProps & {
    options: OptionType[];
    placeholder?: string;
    isDisabled?: boolean;
    hasError?: boolean;
    'aria-required'?: boolean;
  }
> = ({
  field,
  form,
  options,
  placeholder,
  isDisabled = false,
  hasError = false,
  'aria-required': ariaRequired = false,
}) => {
  const selectedOption = options.find((option) => option.value === field.value) || null;

  const handleChange = async (selectedOption: OptionType | null) => {
    const newValue = selectedOption?.value || '';
    try {
      // Set value first and wait for it to update in Formik's state
      await form.setFieldValue(field.name, newValue, false);
      // Then mark as touched and run validation with the new value
      await form.setFieldTouched(field.name, true, true);
    } catch (error) {
      // Fallback: set value without validation if something fails
      console.error('Error updating select field:', field.name, error);
      form.setFieldValue(field.name, newValue, false);
    }
  };

  const handleBlur = () => {
    // Mark as touched and validate on blur
    // This ensures validation runs if user clicks away without selecting
    form.setFieldTouched(field.name, true, true);
  };

  return (
    <Select<OptionType>
      name={field.name}
      inputId={field.name}
      instanceId={field.name} // This is required to prevent multiple instances of the same select field (Client and Server Side Rendering) Hydration issues
      value={selectedOption}
      onChange={handleChange}
      onBlur={handleBlur}
      options={options}
      placeholder={placeholder}
      isDisabled={isDisabled}
      styles={getSelectStyles(hasError, isDisabled)}
      components={{ DropdownIndicator }}
      classNamePrefix="react-select"
      data-automation={`selectField-${field.name}`}
      openMenuOnFocus={true}
      aria-label={placeholder}
      aria-describedby={hasError ? `${field.name}-error` : undefined}
      aria-required={ariaRequired}
      menuPosition="absolute"
      menuPortalTarget={null}
      isSearchable={false}
      aria-controls={field.name}
      menuPlacement="bottom"
    />
  );
};

export const SelectField: React.FC<SelectFieldProps> = ({
  field,
  hasError = false,
  value,
  onChange,
  onBlur,
  errors,
  touched,
  isStandalone = false,
  hideLabel = false,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;
  const placeholder = fieldConfig?.placeholderText?.value || 'Select an option...';
  if (!fieldName) return null;

  // Transform Sitecore options to react-select format
  const options: OptionType[] = (fieldConfig?.selectOptionsAndSection || []).map((option) => ({
    value: (option.fields?.value as SitecoreField<string>)?.value || '',
    label: (option.fields?.text as SitecoreField<string>)?.value || '',
  }));

  const StandaloneSelect: React.FC = () => {
    const selectedOption = options.find((option) => option.value === value) || null;

    const handleChange = (selectedOption: OptionType | null) => {
      const newValue = selectedOption?.value || '';
      onChange?.(newValue);
    };

    const handleBlur = () => {
      onBlur?.();
    };

    return (
      <Select<OptionType>
        name={fieldName}
        inputId={fieldName}
        instanceId={fieldName}
        value={selectedOption}
        onChange={handleChange}
        onBlur={handleBlur}
        options={options}
        placeholder={placeholder}
        isDisabled={disabled}
        styles={getSelectStyles(hasError, disabled || false)}
        components={{ DropdownIndicator }}
        classNamePrefix="react-select"
        data-automation={`selectField-${fieldName}`}
        openMenuOnFocus={true}
        aria-label={placeholder}
        aria-describedby={hasError ? `${fieldName}-error` : undefined}
        aria-required={required}
        menuPosition="absolute"
        menuPortalTarget={null}
        isSearchable={false}
        aria-controls={fieldName}
        menuPlacement="bottom"
      />
    );
  };

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {isStandalone ? (
        <StandaloneSelect />
      ) : (
        <Field
          name={fieldName}
          component={CustomSelectField}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          hasError={hasError}
          aria-required={required}
        />
      )}
    </FormFieldWrapper>
  );
};

export default SelectField;
